import { create } from 'zustand';

export interface ErrorCode {
  code: string;
  label: string;
  explanation: string;
}

export interface EditItem {
  type: 'REPLACE' | 'DELETE' | 'INSERT' | 'MOVE';
  from_span: [number, number];
  to_span?: [number, number];
  original: string;
  correction: string;
  // New: array of error codes for stacked display (supports multiple errors per edit)
  error_codes?: ErrorCode[];
  // Legacy: single error code (for backwards compatibility)
  error_code?: string;
  errant_type?: string;
  explanation: string;
  example?: string;
  label?: string;
  group_id?: number;
}

export interface Analysis {
  id: number;
  original_text: string;
  corrected_text: string;
  edits: EditItem[];
  edit_count: number;
  error_codes: Record<string, number>;
  model_used: string;
  provider_used: string;
  processing_time_ms: number | null;
  created_at: string;
}

interface EditorState {
  // Text content
  originalText: string;
  correctedText: string;

  // Analysis state
  isAnalyzing: boolean;
  analysis: Analysis | null;
  edits: EditItem[];
  appliedEdits: Set<number>;

  // UI state
  activeEditIndex: number | null;
  hoveredEditIndex: number | null;
  showTooltip: boolean;
  tooltipPosition: { x: number; y: number } | null;

  // History for undo - stores full state at each point
  history: { text: string; edits: EditItem[]; appliedIndices: number[] }[];
  historyIndex: number;

  // Actions
  setOriginalText: (text: string) => void;
  setCorrectedText: (text: string) => void;
  setAnalysis: (analysis: Analysis) => void;
  setIsAnalyzing: (isAnalyzing: boolean) => void;
  applyEdit: (index: number) => void;
  applyGroupEdits: (groupId: number) => void;
  rejectEdit: (index: number) => void;
  setActiveEdit: (index: number | null) => void;
  setHoveredEdit: (index: number | null) => void;
  setTooltipPosition: (position: { x: number; y: number } | null) => void;
  undo: () => void;
  redo: () => void;
  reset: () => void;
  acceptAll: () => void;
}

export const useEditorStore = create<EditorState>((set, get) => ({
  // Initial state
  originalText: '',
  correctedText: '',
  isAnalyzing: false,
  analysis: null,
  edits: [],
  appliedEdits: new Set(),
  activeEditIndex: null,
  hoveredEditIndex: null,
  showTooltip: false,
  tooltipPosition: null,
  history: [],
  historyIndex: -1,

  // Actions
  setOriginalText: (text: string) => {
    const { edits } = get();
    // Log when originalText is being set while edits exist (shouldn't happen)
    if (edits.length > 0) {
      console.warn('[setOriginalText] Called while edits exist!', {
        textLength: text.length,
        editsCount: edits.length,
        stackTrace: new Error().stack
      });
    }
    set({ originalText: text });
  },

  setCorrectedText: (text: string) => {
    set({ correctedText: text });
  },

  setAnalysis: (analysis: Analysis) => {
    set({
      analysis,
      correctedText: analysis.corrected_text,
      edits: analysis.edits,
      appliedEdits: new Set(),
      history: [{ text: get().originalText, edits: analysis.edits, appliedIndices: [] }],
      historyIndex: 0,
    });
  },

  setIsAnalyzing: (isAnalyzing: boolean) => {
    set({ isAnalyzing });
  },

  applyEdit: (index: number) => {
    console.log('[applyEdit] ========== START ==========');
    console.log('[applyEdit] Called with index:', index);
    console.log('[applyEdit] Timestamp:', Date.now());
    const { edits, appliedEdits, originalText, history, historyIndex } = get();
    console.log('[applyEdit] Current state - appliedEdits:', Array.from(appliedEdits));
    console.log('[applyEdit] Current state - originalText length:', originalText.length);
    console.log('[applyEdit] Current state - edits count:', edits.length);

    const edit = edits[index];
    console.log('[applyEdit] Edit at index:', edit);
    console.log('[applyEdit] Already applied:', appliedEdits.has(index));
    if (!edit || appliedEdits.has(index)) {
      console.log('[applyEdit] Aborting - edit not found or already applied');
      console.log('[applyEdit] ========== END (aborted) ==========');
      return;
    }

    const newApplied = new Set(appliedEdits);
    newApplied.add(index);

    let [start, end] = edit.from_span;
    let newText = originalText;

    // Verify we're operating on the correct text
    const actualText = originalText.slice(start, end);
    const expectedText = edit.original || '';

    // If position doesn't match expected text, try to find it (case-insensitive comparison)
    if (expectedText && actualText.toLowerCase() !== expectedText.toLowerCase()) {
      console.warn(`Position mismatch: expected "${expectedText}" at [${start},${end}], found "${actualText}"`);
      // Try case-insensitive search
      const lowerOriginal = originalText.toLowerCase();
      const lowerExpected = expectedText.toLowerCase();
      const foundIndex = lowerOriginal.indexOf(lowerExpected);
      if (foundIndex !== -1) {
        console.log(`Found "${expectedText}" at position ${foundIndex} (case-insensitive)`);
        start = foundIndex;
        end = foundIndex + expectedText.length;
      } else {
        console.error(`Cannot find "${expectedText}" in text, aborting edit`);
        return;
      }
    }

    // Apply the edit based on type
    if (edit.type === 'REPLACE') {
      newText = originalText.slice(0, start) + edit.correction + originalText.slice(end);
    } else if (edit.type === 'DELETE') {
      newText = originalText.slice(0, start) + originalText.slice(end);
    } else if (edit.type === 'INSERT') {
      // For INSERT, start === end (insertion point)
      // Add proper spacing around inserted text
      const charBefore = start > 0 ? originalText[start - 1] : '';
      const charAfter = start < originalText.length ? originalText[start] : '';
      let insertText = edit.correction;

      // Punctuation pattern - don't add space before these
      const punctuationStart = /^[.,!?;:)\]}"']/;
      // Punctuation pattern - don't add space after these
      const punctuationEnd = /[(\[{"']$/;

      // Add leading space if: prev char exists, is not space, correction doesn't start with space or punctuation
      const needsLeadingSpace = charBefore &&
                                charBefore !== ' ' &&
                                !insertText.startsWith(' ') &&
                                !punctuationStart.test(insertText);
      if (needsLeadingSpace) {
        insertText = ' ' + insertText;
      }

      // Add trailing space if: next char is a letter/digit (not a space), correction doesn't end with space or opening punctuation
      const needsTrailingSpace = charAfter &&
                                 charAfter !== ' ' &&
                                 /[a-zA-Z0-9]/.test(charAfter) &&
                                 !insertText.endsWith(' ') &&
                                 !punctuationEnd.test(insertText);
      if (needsTrailingSpace) {
        insertText = insertText + ' ';
      }

      newText = originalText.slice(0, start) + insertText + originalText.slice(start);
    } else if (edit.type === 'MOVE') {
      const toPos = edit.to_span ? edit.to_span[0] : 0;
      const movedText = edit.correction || originalText.slice(start, end);

      // Helper to add spacing around moved text at destination
      const addMoveSpacing = (text: string, pos: number, content: string): string => {
        const charBefore = pos > 0 ? text[pos - 1] : '';
        const charAfter = pos < text.length ? text[pos] : '';
        let insertText = content.trim(); // Trim the moved text first

        // Add leading space if needed (not if prev char is space)
        if (charBefore && charBefore !== ' ' && !/^[.,!?;:)\]}"']/.test(insertText)) {
          insertText = ' ' + insertText;
        }

        // Add trailing space if needed (not if next char is already a space)
        if (charAfter && charAfter !== ' ' && /[a-zA-Z0-9]/.test(charAfter) && !insertText.endsWith(' ')) {
          insertText = insertText + ' ';
        }

        return text.slice(0, pos) + insertText + text.slice(pos);
      };

      // Helper to clean up spacing after removing text
      const cleanupAfterRemove = (text: string, pos: number): string => {
        // If we have double spaces at the removal point, collapse to single
        if (pos > 0 && pos < text.length && text[pos - 1] === ' ' && text[pos] === ' ') {
          return text.slice(0, pos) + text.slice(pos + 1);
        }
        return text;
      };

      if (toPos <= start) {
        // Moving backwards: insert at destination first, then remove from source
        const temp = addMoveSpacing(originalText, toPos, movedText);
        // Calculate how much the positions shifted due to spacing
        const insertedLength = temp.length - originalText.length + (end - start);
        const shiftedStart = start + (temp.length - originalText.length);
        const shiftedEnd = end + (temp.length - originalText.length);
        const afterRemove = temp.slice(0, shiftedStart) + temp.slice(shiftedEnd);
        newText = cleanupAfterRemove(afterRemove, shiftedStart);
      } else {
        // Moving forwards: remove from source first, then insert at destination
        const temp = originalText.slice(0, start) + originalText.slice(end);
        const cleanedTemp = cleanupAfterRemove(temp, start);
        const shiftedToPos = toPos - (end - start) - (temp.length - cleanedTemp.length);
        newText = addMoveSpacing(cleanedTemp, shiftedToPos, movedText);
      }
    }

    // Final cleanup: remove any double spaces that may have been introduced
    // This is a safety net to catch edge cases
    const doubleSpaceRegex = /  +/g;
    if (newText && doubleSpaceRegex.test(newText)) {
      newText = newText.replace(doubleSpaceRegex, ' ');
      console.log('[applyEdit] Cleaned up double spaces');
    }

    // Calculate how positions should shift for remaining edits
    // For MOVE operations, we need special handling since text moves from one place to another
    const lengthDiff = newText.length - originalText.length;

    // Helper to adjust a position based on the edit that was applied
    const adjustPosition = (pos: number): number => {
      if (edit.type === 'MOVE' && edit.to_span) {
        const toPos = edit.to_span[0];
        const sourceStart = start;
        const sourceEnd = end;
        const movedLength = (edit.correction || edit.original || '').trim().length;

        if (toPos < sourceStart) {
          // Moving backwards: insert at toPos, then remove from shifted source
          if (pos < toPos) {
            // Before insertion point: no change
            return pos;
          } else if (pos >= toPos && pos < sourceStart) {
            // Between insertion and original source: shift right by inserted length
            // Account for spacing that may have been added
            const insertedWithSpacing = movedLength + (pos === toPos ? 0 : 1); // +1 for space after inserted word
            return pos + insertedWithSpacing;
          } else if (pos >= sourceStart && pos < sourceEnd) {
            // Inside the moved text: map to new position (this shouldn't happen for remaining edits)
            return toPos + (pos - sourceStart);
          } else {
            // After original source: net effect is lengthDiff
            return pos + lengthDiff;
          }
        } else {
          // Moving forwards: remove from source, then insert at shifted toPos
          if (pos < sourceStart) {
            // Before source: no change
            return pos;
          } else if (pos >= sourceStart && pos < sourceEnd) {
            // Inside the moved text: map to new position
            return toPos - (sourceEnd - sourceStart) + (pos - sourceStart);
          } else if (pos >= sourceEnd && pos < toPos) {
            // Between source and destination: shift left by removed length
            return pos - (sourceEnd - sourceStart);
          } else {
            // After destination: net effect is lengthDiff
            return pos + lengthDiff;
          }
        }
      } else {
        // For non-MOVE edits, simple adjustment
        if (pos >= end) {
          return pos + lengthDiff;
        }
        return pos;
      }
    };

    // Adjust positions of remaining edits
    const newEdits = edits.map((e, i) => {
      if (i === index || newApplied.has(i)) return e;

      const newStart = adjustPosition(e.from_span[0]);
      const newEnd = adjustPosition(e.from_span[1]);
      let newFromSpan: [number, number] = [newStart, newEnd];
      let newToSpan = e.to_span;

      // Adjust to_span if it exists
      if (e.to_span) {
        const newToStart = adjustPosition(e.to_span[0]);
        const newToEnd = adjustPosition(e.to_span[1]);
        newToSpan = [newToStart, newToEnd] as [number, number];
      }

      if (newFromSpan[0] !== e.from_span[0] || newFromSpan[1] !== e.from_span[1] ||
          (newToSpan && e.to_span && (newToSpan[0] !== e.to_span[0] || newToSpan[1] !== e.to_span[1]))) {
        return { ...e, from_span: newFromSpan, to_span: newToSpan };
      }
      return e;
    });

    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push({
      text: newText,
      edits: newEdits,
      appliedIndices: Array.from(newApplied),
    });

    console.log('[applyEdit] Final state - newText:', newText);
    console.log('[applyEdit] Final state - newApplied:', Array.from(newApplied));
    console.log('[applyEdit] Final state - newEdits positions:', newEdits.map((e, i) => ({
      index: i,
      from_span: e.from_span,
      original: e.original,
      applied: newApplied.has(i)
    })));

    set({
      originalText: newText,
      edits: newEdits,
      appliedEdits: newApplied,
      history: newHistory,
      historyIndex: historyIndex + 1,
    });
    console.log('[applyEdit] ========== END (success) ==========');
  },

  applyGroupEdits: (groupId: number) => {
    const { edits, appliedEdits, originalText, history, historyIndex } = get();

    // Find all edits in this group
    const groupEdits = edits
      .map((edit, index) => ({ edit, index }))
      .filter(({ edit, index }) => edit.group_id === groupId && !appliedEdits.has(index));

    if (groupEdits.length === 0) return;

    const newApplied = new Set(appliedEdits);
    let newText = originalText;

    // Sort edits by position (end to start) to avoid position shifts during application
    const sortedEdits = [...groupEdits].sort((a, b) => b.edit.from_span[0] - a.edit.from_span[0]);

    // Check for MOVE + INSERT pair (common pattern for word reordering with replacement)
    const moveEdit = groupEdits.find(g => g.edit.type === 'MOVE');
    const insertEdit = groupEdits.find(g => g.edit.type === 'INSERT');

    if (moveEdit && insertEdit) {
      // Special handling for MOVE + INSERT pairs
      newApplied.add(moveEdit.index);
      newApplied.add(insertEdit.index);

      const [moveStart, moveEnd] = moveEdit.edit.from_span;
      const toPos = moveEdit.edit.to_span ? moveEdit.edit.to_span[0] : 0;
      const movedText = moveEdit.edit.correction || originalText.slice(moveStart, moveEnd);
      const insertText = insertEdit.edit.correction;

      if (toPos <= moveStart) {
        // Moving backwards: insert moved text at destination, replace source with insert text
        // Step 1: Replace source with insert text
        let temp = originalText.slice(0, moveStart) + insertText + originalText.slice(moveEnd);
        // Step 2: Insert moved text at destination (position unchanged since it's before source)
        newText = temp.slice(0, toPos) + movedText + temp.slice(toPos);
      } else {
        // Moving forwards: replace source with insert text, then insert moved text at destination
        // Step 1: Insert moved text at destination first
        let temp = originalText.slice(0, toPos) + movedText + originalText.slice(toPos);
        // Step 2: Replace source with insert text (positions shifted by movedText.length)
        const shiftedStart = moveStart;
        const shiftedEnd = moveEnd;
        newText = temp.slice(0, shiftedStart) + insertText + temp.slice(shiftedEnd + movedText.length);
      }
    } else {
      // Apply edits from end to start so positions don't shift
      for (const { edit, index } of sortedEdits) {
        newApplied.add(index);

        // Find correct position (verify or search)
        let [start, end] = edit.from_span;
        const expectedText = edit.original || '';
        const actualText = newText.slice(start, end);

        // Case-insensitive comparison for position verification
        if (expectedText && actualText.toLowerCase() !== expectedText.toLowerCase()) {
          // Try case-insensitive search
          const lowerText = newText.toLowerCase();
          const lowerExpected = expectedText.toLowerCase();
          const foundIndex = lowerText.indexOf(lowerExpected);
          if (foundIndex !== -1) {
            start = foundIndex;
            end = foundIndex + expectedText.length;
          } else {
            console.error(`Cannot find "${expectedText}" in text for group edit`);
            continue;
          }
        }

        if (edit.type === 'REPLACE') {
          newText = newText.slice(0, start) + edit.correction + newText.slice(end);
        } else if (edit.type === 'DELETE') {
          newText = newText.slice(0, start) + newText.slice(end);
        } else if (edit.type === 'INSERT') {
          // Add proper spacing around inserted text
          const charBefore = start > 0 ? newText[start - 1] : '';
          const charAfter = start < newText.length ? newText[start] : '';
          let insertText = edit.correction;

          // Punctuation pattern - don't add space before these
          const punctuationStart = /^[.,!?;:)\]}"']/;
          // Punctuation pattern - don't add space after these
          const punctuationEnd = /[(\[{"']$/;

          // Add leading space if: prev char exists, is not space, correction doesn't start with space or punctuation
          const needsLeadingSpace = charBefore &&
                                    charBefore !== ' ' &&
                                    !insertText.startsWith(' ') &&
                                    !punctuationStart.test(insertText);
          if (needsLeadingSpace) {
            insertText = ' ' + insertText;
          }

          // Add trailing space if: next char is a letter/digit, correction doesn't end with space or opening punctuation
          const needsTrailingSpace = charAfter &&
                                     /[a-zA-Z0-9]/.test(charAfter) &&
                                     !insertText.endsWith(' ') &&
                                     !punctuationEnd.test(insertText);
          if (needsTrailingSpace) {
            insertText = insertText + ' ';
          }

          newText = newText.slice(0, start) + insertText + newText.slice(start);
        } else if (edit.type === 'MOVE') {
          const toPos = edit.to_span ? edit.to_span[0] : 0;
          const movedText = edit.correction || newText.slice(start, end);

          // Helper to add spacing around moved text at destination
          const addMoveSpacing = (text: string, pos: number, content: string): string => {
            const charBefore = pos > 0 ? text[pos - 1] : '';
            const charAfter = pos < text.length ? text[pos] : '';
            let insertText = content.trim();

            if (charBefore && charBefore !== ' ' && !/^[.,!?;:)\]}"']/.test(insertText)) {
              insertText = ' ' + insertText;
            }

            if (charAfter && /[a-zA-Z0-9]/.test(charAfter)) {
              insertText = insertText + ' ';
            }

            return text.slice(0, pos) + insertText + text.slice(pos);
          };

          // Helper to clean up spacing after removing text
          const cleanupAfterRemove = (text: string, pos: number): string => {
            if (pos > 0 && pos < text.length && text[pos - 1] === ' ' && text[pos] === ' ') {
              return text.slice(0, pos) + text.slice(pos + 1);
            }
            return text;
          };

          if (toPos <= start) {
            const temp = addMoveSpacing(newText, toPos, movedText);
            const shiftedStart = start + (temp.length - newText.length);
            const shiftedEnd = end + (temp.length - newText.length);
            const afterRemove = temp.slice(0, shiftedStart) + temp.slice(shiftedEnd);
            newText = cleanupAfterRemove(afterRemove, shiftedStart);
          } else {
            const temp = newText.slice(0, start) + newText.slice(end);
            const cleanedTemp = cleanupAfterRemove(temp, start);
            const shiftedToPos = toPos - (end - start) - (temp.length - cleanedTemp.length);
            newText = addMoveSpacing(cleanedTemp, shiftedToPos, movedText);
          }
        }
      }
    }

    // Recalculate positions for remaining edits
    const lengthDiff = newText.length - originalText.length;
    const maxEnd = Math.max(...groupEdits.map(g => g.edit.from_span[1]));

    const newEdits = edits.map((e, i) => {
      if (newApplied.has(i)) return e;

      let newFromSpan = e.from_span;
      let newToSpan = e.to_span;

      // Adjust if after the changed region
      if (e.from_span[0] >= maxEnd) {
        newFromSpan = [
          e.from_span[0] + lengthDiff,
          e.from_span[1] + lengthDiff
        ] as [number, number];
      }

      if (e.to_span && e.to_span[0] >= maxEnd) {
        newToSpan = [
          e.to_span[0] + lengthDiff,
          e.to_span[1] + lengthDiff
        ] as [number, number];
      }

      if (newFromSpan !== e.from_span || newToSpan !== e.to_span) {
        return { ...e, from_span: newFromSpan, to_span: newToSpan };
      }
      return e;
    });

    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push({
      text: newText,
      edits: newEdits,
      appliedIndices: Array.from(newApplied),
    });

    set({
      originalText: newText,
      edits: newEdits,
      appliedEdits: newApplied,
      history: newHistory,
      historyIndex: historyIndex + 1,
    });
  },

  rejectEdit: (index: number) => {
    const { edits, appliedEdits } = get();
    const newApplied = new Set(appliedEdits);
    newApplied.delete(index);
    set({ appliedEdits: newApplied });
  },

  setActiveEdit: (index: number | null) => {
    set({ activeEditIndex: index, showTooltip: index !== null });
  },

  setHoveredEdit: (index: number | null) => {
    set({ hoveredEditIndex: index });
  },

  setTooltipPosition: (position: { x: number; y: number } | null) => {
    set({ tooltipPosition: position });
  },

  undo: () => {
    const { historyIndex, history } = get();
    if (historyIndex > 0) {
      const prev = history[historyIndex - 1];
      // Restore full state: text, edits with positions, and which are applied
      set({
        historyIndex: historyIndex - 1,
        originalText: prev.text,
        edits: prev.edits,
        appliedEdits: new Set(prev.appliedIndices),
      });
    }
  },

  redo: () => {
    const { historyIndex, history } = get();
    if (historyIndex < history.length - 1) {
      const next = history[historyIndex + 1];
      // Restore full state: text, edits with positions, and which are applied
      set({
        historyIndex: historyIndex + 1,
        originalText: next.text,
        edits: next.edits,
        appliedEdits: new Set(next.appliedIndices),
      });
    }
  },

  reset: () => {
    set({
      originalText: '',
      correctedText: '',
      isAnalyzing: false,
      analysis: null,
      edits: [],
      appliedEdits: new Set(),
      activeEditIndex: null,
      hoveredEditIndex: null,
      showTooltip: false,
      tooltipPosition: null,
      history: [],
      historyIndex: -1,
    });
  },

  acceptAll: () => {
    const { edits, appliedEdits } = get();
    // Apply each unapplied edit in order (from end to start to handle position shifts)
    const unappliedIndices = edits
      .map((_, i) => i)
      .filter(i => !appliedEdits.has(i))
      .sort((a, b) => edits[b].from_span[0] - edits[a].from_span[0]); // Sort by position descending

    // Apply each edit individually (this properly transforms the text)
    for (const index of unappliedIndices) {
      // Check if edit has group_id - if so, apply group together
      const edit = get().edits[index];
      if (!get().appliedEdits.has(index)) {
        if (edit.group_id != null) {
          get().applyGroupEdits(edit.group_id);
        } else {
          get().applyEdit(index);
        }
      }
    }
  },
}));

